import { useEffect, useRef, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Map,
  Camera,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Paperclip,
  FileText,
  X,
  ArrowRight,
} from 'lucide-react';
import { api } from '../../services/api';
import {
  UploadRefused,
  describeUploadError,
  toUploadableImage,
  uploadProblem,
  withUploadType,
} from './bmpUpload';
import { MapCamera } from './components/MapCamera';
import { track } from '../../services/telemetry';

type Sheet = {
  id: string;
  period_month: string;
  filename: string | null;
  mime_type: string | null;
  sheet_label: string | null;
  image_url: string | null;
  uploaded_at?: string | null;
};

const isPdf = (sheet: Sheet) =>
  sheet.mime_type === 'application/pdf' ||
  (sheet.filename ?? '').toLowerCase().endsWith('.pdf');

type Status = {
  period: string;
  satisfied: boolean;
  due?: boolean;
  routine_since_map?: number;
  has_upload: boolean;
  has_bypass: boolean;
  uploads?: Sheet[];
  sheet_count?: number;
};

/**
 * BMP-map gate (EP-SWPPP WS-4). Shown instead of the inspection flow when an
 * updated map is due — every four routine visits (Jim 2026-10-07), never on
 * the first — until it is uploaded (or overridden). Jim: "you're
 * not doing the inspection until you put that BMP map in." Includes framing
 * guidance and the annoying-but-present override.
 *
 * A month's map is a SET of sheets — big sites split the map across phases or
 * pages — so this uploads many at once and then parks on a review screen
 * (thumbnails + add/remove) until the inspector taps Continue. The parent
 * keeps the gate mounted until then; auto-dismissing on the first sheet would
 * make sheets 2..N unreachable.
 */

export function BmpMapGate({
  siteId,
  onContinue,
  mode = 'monthly',
  onBack,
}: {
  siteId: string;
  onContinue?: () => void;
  // 'final' (Jim 2026-08-20): a declared final inspection cannot be
  // submitted without a BMP-map photo taken TODAY — no bypass. "I'm not
  // going to let you submit this inspection without giving me that BMP."
  mode?: 'monthly' | 'final';
  onBack?: () => void;
}) {
  const qc = useQueryClient();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [showBypass, setShowBypass] = useState(false);
  const [reason, setReason] = useState('');

  // Usage telemetry: the gate blocked an inspection start (once per mount).
  useEffect(() => {
    if (siteId) track('bmp_gate_shown', { site_id: siteId });
  }, [siteId]);

  const key = ['bmp-map-status', siteId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const r = await api.get<Status>(`/sites/${siteId}/bmp-map/status`);
      return r.data;
    },
    enabled: !!siteId,
  });

  const uploadMut = useMutation({
    mutationFn: async (files: File[]) => {
      // Refuse a wrong pick here, with the reason, before anything is sent.
      const problem = files.map(uploadProblem).find(Boolean);
      if (problem) throw new UploadRefused(problem);
      const form = new FormData();
      for (const file of files) {
        form.append('files', await toUploadableImage(withUploadType(file)));
      }
      // Explicit multipart header: the api instance defaults to application/json,
      // and axios serializes FormData to JSON ({"files":{}}) under that header.
      const r = await api.post(`/sites/${siteId}/bmp-map`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const removeMut = useMutation({
    mutationFn: async (uploadId: string) => {
      const r = await api.delete(`/sites/${siteId}/bmp-map/${uploadId}`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const bypassMut = useMutation({
    retry: 1,
    mutationFn: async () => {
      const r = await api.post(`/sites/${siteId}/bmp-map/bypass`, {
        reason: reason.trim(),
      });
      return r.data;
    },
    onSuccess: () => {
      track('bmp_gate_override', { site_id: siteId });
      qc.invalidateQueries({ queryKey: key });
      onContinue?.();
    },
  });

  const period = data?.period ?? '';
  const sinceMap = data?.routine_since_map ?? 0;
  const sheets = data?.uploads ?? [];
  const isFinal = mode === 'final';
  // Jim 10/7: when the ask is due, the sheets already on file are the OLD
  // map (four routine visits have gone by since). They don't answer the ask;
  // a fresh upload or a bypass resets `due` and the review screen takes over.
  const stale = !isFinal && data?.due === true;
  const hasSheets = sheets.length > 0 && !stale;
  const todayLocal = new Date().toLocaleDateString('en-CA');
  const hasTodaySheet = sheets.some(
    (sh) =>
      sh.uploaded_at &&
      new Date(
        sh.uploaded_at + (sh.uploaded_at.endsWith('Z') ? '' : 'Z')
      ).toLocaleDateString('en-CA') === todayLocal
  );
  // Monthly gate: any sheet this month. Final gate: a sheet from TODAY —
  // last month's map on file doesn't document the completed site.
  const canContinue = isFinal ? hasTodaySheet : hasSheets;

  // Jim 2026-10-14: the guided in-app camera first; the phone's own camera if the
  // browser will not open one.
  const [showCamera, setShowCamera] = useState(false);
  const openCamera = () => setShowCamera(true);
  const cameraUnavailable = useCallback(() => {
    setShowCamera(false);
    cameraRef.current?.click();
  }, []);

  const pickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files ?? []);
    if (chosen.length) uploadMut.mutate(chosen);
    // Allow re-picking the same file after a remove.
    e.target.value = '';
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 flex items-center justify-center">
      {showCamera && (
        <MapCamera
          onDone={(files) => {
            setShowCamera(false);
            if (files.length) uploadMut.mutate(files);
          }}
          onCancel={() => setShowCamera(false)}
          onUnavailable={cameraUnavailable}
        />
      )}
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-amber-300 dark:border-amber-700 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Map className="w-6 h-6 text-amber-600" />
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
            {isFinal
              ? 'Final inspection — capture the completed BMP map'
              : hasSheets
                ? 'Updated BMP map'
                : 'Update the BMP map first'}
          </h1>
        </div>

        {!hasSheets && (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {isFinal
              ? "This is the site's final inspection. Take a photo of the completed BMP map now — the inspection can't be submitted without it."
              : `${
                  sinceMap >= 2
                    ? `${sinceMap} routine inspections since the last BMP map. `
                    : ''
                }Before this inspection, upload a photo of the BMP map as it stands today. You can't start until it's in.`}
          </p>
        )}

        {/* Actions sit right under the heading: on a desktop the tips box and
            the sheet grid pushed "Take photo" to the bottom of the card
            (Jim 2026-09-04 [80:44]). */}
        {/* Two inputs on purpose: `capture` forces the camera on iOS, so a
            single control could never reach the Files app to attach a PDF. */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          className="hidden"
          onChange={pickFiles}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={pickFiles}
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={openCamera}
            disabled={uploadMut.isPending}
            className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md disabled:opacity-50 ${
              hasSheets
                ? 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                : 'text-white bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {uploadMut.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
            {hasSheets ? 'Take another' : 'Take photo'}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadMut.isPending}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 disabled:opacity-50"
          >
            <Paperclip className="w-4 h-4" />
            Attach file
          </button>
        </div>
        {!hasSheets && (
          <div className="rounded-lg bg-slate-50 dark:bg-slate-700/40 p-3 text-xs text-slate-500 dark:text-slate-400 space-y-1">
            <p className="font-medium text-slate-600 dark:text-slate-300">
              For a usable photo:
            </p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Lay the map flat and fill the frame with it.</li>
              <li>Avoid glare and shadows; shoot straight down.</li>
              <li>Make sure the latest markups are legible.</li>
              <li>
                Several sheets? Add every one — you can shoot or attach them all
                at once.
              </li>
              <li>
                Already have the map as a PDF or image file? Attach it instead
                of photographing it.
              </li>
            </ul>
          </div>
        )}

        {hasSheets && (
          <div className="space-y-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {sheets.length === 1
                ? `1 sheet saved for ${period}.`
                : `${sheets.length} sheets saved for ${period}.`}{' '}
              Add the rest if this site's map has more.
            </p>
            <ul className="grid grid-cols-3 gap-2">
              {sheets.map((sheet, i) => (
                <li key={sheet.id} className="relative">
                  {isPdf(sheet) ? (
                    <a
                      href={sheet.image_url ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full h-24 flex flex-col items-center justify-center gap-1 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 px-1"
                    >
                      <FileText className="w-6 h-6 text-slate-500 dark:text-slate-300" />
                      <span className="text-[10px] text-slate-500 dark:text-slate-300 truncate max-w-full">
                        PDF
                      </span>
                    </a>
                  ) : (
                    <img
                      src={sheet.image_url ?? undefined}
                      alt={sheet.sheet_label ?? `BMP map sheet ${i + 1}`}
                      className="w-full h-24 object-cover rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700"
                    />
                  )}
                  <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium">
                    {sheet.sheet_label ?? `Sheet ${i + 1}`}
                  </span>
                  <button
                    onClick={() => removeMut.mutate(sheet.id)}
                    disabled={removeMut.isPending}
                    aria-label={`Remove sheet ${i + 1}`}
                    className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-500 hover:text-danger-600 shadow-sm disabled:opacity-50"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploadMut.isPending && (
          <p className="text-xs text-slate-500" data-testid="bmp-uploading">
            Saving the map… keep this screen open.
          </p>
        )}
        {uploadMut.isError && (
          <p className="text-sm text-red-600" role="alert">
            {describeUploadError(uploadMut.error)}
          </p>
        )}
        {removeMut.isError && (
          <p className="text-xs text-red-600">
            Couldn't remove that sheet — try again.
          </p>
        )}

        {isFinal && hasSheets && !hasTodaySheet && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            The sheets on file are from earlier in {period}. The final
            inspection needs a photo of the map as it looks today.
          </p>
        )}

        {canContinue && (
          <button
            onClick={() => onContinue?.()}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            {isFinal
              ? 'Continue — submit final inspection'
              : 'Continue to inspection'}
            <ArrowRight className="w-4 h-4" />
          </button>
        )}

        {isFinal && (
          <button
            onClick={() => onBack?.()}
            className="w-full text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            Go back to the inspection (or uncheck "final inspection")
          </button>
        )}

        {!isFinal &&
          !hasSheets &&
          (!showBypass ? (
            <button
              onClick={() => setShowBypass(true)}
              className="w-full text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              I can't do it right now
            </button>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5" />
                This is logged as overdue until a real map is uploaded.
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why can't the BMP map be uploaded right now?"
                rows={2}
                className="w-full px-2 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
              <button
                onClick={() => reason.trim() && bypassMut.mutate()}
                disabled={!reason.trim() || bypassMut.isPending}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-amber-400 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
              >
                {bypassMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Continue without the map (overdue)
              </button>
              {bypassMut.isError && (
                <p className="text-xs text-danger-600 dark:text-danger-400">
                  Couldn't record the override — check your connection and tap
                  the button again.
                </p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
